import { bracketSlotName } from "@/utils/league-bracket";

export function goalsBySquadId (games, squadIds) {
  const totals = {};
  for (const id of squadIds) totals[id] = 0;
  for (const game of games || []) {
    const scores = game.scores || {};
    for (const [id, score] of Object.entries(scores)) {
      if (Object.prototype.hasOwnProperty.call(totals, id)) {
        totals[id] += score;
      }
    }
  }
  return totals;
}

export function isDecided (totals, pointsToWin) {
  return Object.values(totals || {}).some(total => total >= pointsToWin);
}

export function winningSquadId (totals, pointsToWin) {
  const match = Object.entries(totals || {}).find(([, total]) => total >= pointsToWin);
  return match ? match[0] : null;
}

export function sameSquadPair (pairA, pairB) {
  if (!pairA || !pairB || pairA.length !== 2 || pairB.length !== 2) return false;
  const [a, b] = pairB;
  return pairA.includes(a) && pairA.includes(b) && a !== b;
}

export function findScheduleEventByEncounter (scheduleEvents, squadIds, encounterNumber) {
  const events = (scheduleEvents || []).filter(event => {
    if (event.bracketSlot) return false;
    return sameSquadPair([event.homeSquadId, event.awaySquadId], squadIds);
  });
  return events[encounterNumber - 1] || null;
}

export function findScheduleEventByBracketSlot (scheduleEvents, bracketSlot) {
  return (scheduleEvents || []).find(event => event.bracketSlot === bracketSlot) || null;
}

// Renders a bracket slot as a display label. `slotSquadIds` is the pair [homeId, awayId] from
// league.bracketSquads; a side is a squad when known, TBD when its feeder race is undecided, and
// (bye) when the slot is a structural bye (a single squad auto-advances). `squads` is used to
// resolve titles. `numRounds` is the total knockout round count and drives the friendly round
// name prefix ("Semi-final", "Round of 16", ...) instead of the raw slot id.
export function bracketEventLabel (event, slotSquadIds, squads, numRounds) {
  const title = squadId => {
    if (squadId) {
      const squad = squads.find(s => s.id === squadId);
      if (squad) return squad.title;
    }
    return "TBD";
  };
  const prefix = bracketSlotName(event.bracketSlot, numRounds);
  if (event.bracketSlot === "THIRD") {
    if (slotSquadIds && slotSquadIds.length === 2) {
      return `${prefix}: ${title(slotSquadIds[0])} vs ${title(slotSquadIds[1])}`;
    }
    return `${prefix}: TBD vs TBD`;
  }
  if (slotSquadIds && slotSquadIds.length) {
    const home = title(slotSquadIds[0]);
    const away = slotSquadIds[1] ? title(slotSquadIds[1]) : null;
    if (!away && event.awaySeed == null) return `${prefix}: ${home} (bye)`;
    return `${prefix}: ${home} vs ${away || "TBD"}`;
  }
  return event.awaySeed == null
    ? `${prefix}: TBD (bye)`
    : `${prefix}: TBD vs TBD`;
}

const HOME_MATCHUP_PATTERN = [
  [0, 2],
  [1, 2],
  [0, 1],
  [0, 2],
  [1, 2],
  [0, 1],
  [0, 2],
  [0, 1],
  [1, 2]
];

const AWAY_MATCHUP_PATTERN = [
  [0, 2],
  [1, 2],
  [0, 1],
  [1, 2],
  [0, 1],
  [0, 2],
  [0, 1],
  [1, 2],
  [0, 2]
];

export function matchupIndices (gameNumber) {
  const i = (gameNumber - 1) % 9;
  return [HOME_MATCHUP_PATTERN[i], AWAY_MATCHUP_PATTERN[i]];
}

// The two teams of a game. Active (unscored) games always reflect the live
// squad rosters via the game-number rotation. Inactive (scored) games use the members that
// were snapshotted when the game was scored so later squad changes don't rewrite history.
export function gameTeams (race, game, gameNumber) {
  if (game.active) {
    return race.squads.map((squad, squadIndex) =>
      squad.pickTeamMembers(squadIndex === 0, gameNumber)
    );
  }
  const members = race.league.members;
  return [
    findTeamMembersById(game.homeMemberIds, members),
    findTeamMembersById(game.awayMemberIds, members)
  ];
}

// Resolves a stored side (member ids in the two team slots, only prefix-length present) to
// member objects. Trailing slots are padded with a placeholder name, as squad.pickTeamMembers does.
function findTeamMembersById (memberIds, members) {
  const side = [];
  for (let memberPosition = 0; memberPosition < 2; memberPosition++) {
    const id = memberIds[memberPosition];
    const member = id ? members.find(m => m.id === id) : null;
    side.push(member || { name: `Player ${memberPosition + 1}` });
  }
  return side;
}

// A fingerprint of a single persisted race, matched against the fingerprints of the expected
// schedule documents. Round-robin races are keyed by sorted squad ids + encounter number;
// bracket races by their slot. A race that no longer maps to any schedule event is "extra".
export function raceFingerprint (race) {
  if (race.bracketSlot) return `b:${race.bracketSlot}`;
  return `r:${[...race.squadIds].sort().join("|")}:#${race.encounterNumber}`;
}

// True when the persisted races are exactly the schedule's events: same round-robin pairs with
// encounters in order, and the same bracket slots. Any mismatch (settings changed, races not yet
// generated) means the races must be regenerated to match the current settings. Encounter numbers
// are unique per pair in both the expected and persisted documents, so a plain set comparison is
// exact: the lengths match and every persisted race is one of the expected identities.
export function racesMatchSchedule (scheduleEvents, races) {
  const expected = new Set(raceDocumentsForSchedule(scheduleEvents).map(raceFingerprint));
  const actual = (races || []).map(raceFingerprint);
  return expected.size === actual.length && actual.every(fingerprint => expected.has(fingerprint));
}

// False until a game in any race has actually been scored. Games are created with `active: true`
// and flip to `active: false` the moment a score is submitted, so an inactive game is the precise
// signal that results exist somewhere. Once true, regeneration would be destructive (scores would
// be lost), so callers should stop and lock schedule-shape controls.
export function hasAnyScoredGame (races) {
  return (races || []).some(race => race.inactiveGames && race.inactiveGames.length > 0);
}

// True while the squad is mid-race: it takes part in a race that has at least one scored game
// but is not yet decided. Changing the roster now would seed any newly added member from the
// default { mu: 0, sigma: 0 } (their startOfRaceMu/Sigma were never snapshotted), and the next
// scored game's full replay would overwrite their real scorecard with that near-zero rating.
export function squadHasInProgressRace (league, squad) {
  return league.races.some(race =>
    race.squads.some(s => s.id === squad.id) &&
    race.inactiveGames.length > 0 &&
    !race.decided
  );
}

export function raceDocumentsForSchedule (scheduleEvents) {
  const encounterByPair = new Map();
  const key = pair => [...pair].sort().join("|");
  return (scheduleEvents || [])
    .filter(event => !event.isBlackout)
    .map((event) => {
      if (event.bracketSlot) {
        return {
          squadIds: [],
          encounterNumber: 1,
          bracketSlot: event.bracketSlot,
          homeSeed: event.homeSeed,
          awaySeed: event.awaySeed
        };
      }
      const pair = [event.homeSquadId, event.awaySquadId];
      const pairKey = key(pair);
      const encounterNumber = (encounterByPair.get(pairKey) || 0) + 1;
      encounterByPair.set(pairKey, encounterNumber);
      return { squadIds: [...pair].sort(), encounterNumber };
    });
}
